import React, { useReducer, useContext } from 'react';
import { useUserDispatchers } from '../../../state/user';

import AdditionalPlansScreen from './AdditionalPlansScreen';
import CoreScreen from './CoreScreen';
import InfinityPlanDetails from './PlanDetails/InfinityPlanDetails';
import MaxPlanDetails from './PlanDetails/MaxPlanDetails';
import { AnalyticsContext } from '../../../ModularReduxAnalytics';

type Status =
  | 'AGE_NOT_CONFIRMED'
  | 'AGE_CONFIRMED'
  | 'UPSELL_PLANS'
  | 'MAX_DETAILS'
  | 'INFINITY_DETAILS'
  | 'COMPLETE';

const INITIAL_STATUS: Status = 'AGE_NOT_CONFIRMED';

export type Plan = 'standard' | 'infinity' | 'max';

type Action =
  | {
      type: 'TOGGLE_AGE_CONFIRMATION';
    }
  | {
      type: 'EXPLORE_PLANS_CLICK';
    }
  | {
      type: 'LEARN_MORE';
      plan: Plan;
    }
  | {
      type: 'SELECT_PLAN';
      plan: Plan;
    }
  | {
      type: 'BACK';
    };

const FccSetPlan = () => {
  const { setPreRegPlanSelection, setRole } = useUserDispatchers();
  const { analytics } = useContext(AnalyticsContext);

  const statusReducer = (status: Status, action: Action): Status => {
    if (status === 'AGE_NOT_CONFIRMED') {
      if (action.type === 'TOGGLE_AGE_CONFIRMATION') {
        return 'AGE_CONFIRMED';
      }
    } else if (status === 'AGE_CONFIRMED') {
      if (action.type === 'TOGGLE_AGE_CONFIRMATION') {
        return 'AGE_NOT_CONFIRMED';
      }
      if (action.type === 'EXPLORE_PLANS_CLICK') {
        window.scrollTo(0, 0);
        return 'UPSELL_PLANS';
      }
      if (action.type === 'SELECT_PLAN') {
        return 'COMPLETE';
      }
    } else if (status === 'UPSELL_PLANS') {
      if (action.type === 'BACK') {
        return 'AGE_CONFIRMED';
      }
      if (action.type === 'LEARN_MORE' && action.plan === 'max') {
        return 'MAX_DETAILS';
      }
      if (action.type === 'LEARN_MORE' && action.plan === 'infinity') {
        return 'INFINITY_DETAILS';
      }
      if (action.type === 'SELECT_PLAN') {
        return 'COMPLETE';
      }
    } else if (status === 'MAX_DETAILS' || status === 'INFINITY_DETAILS') {
      if (action.type === 'BACK') {
        return 'UPSELL_PLANS';
      }
      if (action.type === 'SELECT_PLAN') {
        return 'COMPLETE';
      }
    }
    return status;
  };

  const handleStatusTransitionEffects = (
    fromStatus: Status,
    toStatus: Status,
    action: Action
  ): Status => {
    if (
      toStatus === 'UPSELL_PLANS' ||
      toStatus === 'INFINITY_DETAILS' ||
      toStatus === 'MAX_DETAILS' ||
      (fromStatus === 'UPSELL_PLANS' && toStatus === 'AGE_CONFIRMED')
    ) {
      window.scrollTo(0, 0);
    }

    if (toStatus === 'COMPLETE' && action.type === 'SELECT_PLAN') {
      setPreRegPlanSelection({ plan: action.plan });
      setRole('parent');
    }

    return toStatus;
  };

  const statusReducerWithTransitionEffects = (
    status: Status,
    action: Action
  ): Status => {
    const newStatus = statusReducer(status, action);
    handleStatusTransitionEffects(status, newStatus, action);
    return newStatus;
  };

  const [status, dispatch] = useReducer<
    (status: Status, action: Action) => Status
  >(statusReducerWithTransitionEffects, INITIAL_STATUS);

  if (status === 'AGE_NOT_CONFIRMED' || status === 'AGE_CONFIRMED') {
    return (
      <CoreScreen
        ageConfirmed={status === 'AGE_CONFIRMED'}
        onAgeConfirmationToggle={() =>
          dispatch({ type: 'TOGGLE_AGE_CONFIRMATION' })
        }
        onNextClick={() => {
          analytics.trackEvent('Credit Action', {
            eventProps: {
              type: 'Next',
              page: 'FCC in Reg Set Plan'
            }
          });
          dispatch({ type: 'SELECT_PLAN', plan: 'standard' });
        }}
        onExplorePlansClick={() => {
          analytics.trackEvent('Credit Action', {
            eventProps: {
              type: 'Explore Other Greenlight Plans',
              page: 'FCC in Reg Set Plan'
            }
          });
          dispatch({ type: 'EXPLORE_PLANS_CLICK' });
        }}
      />
    );
  }

  if (status === 'UPSELL_PLANS') {
    return (
      <AdditionalPlansScreen
        onPlanSelect={(plan: Plan) => dispatch({ type: 'SELECT_PLAN', plan })}
        onInfoClick={(plan: Plan) => dispatch({ type: 'LEARN_MORE', plan })}
        onBackClick={() => dispatch({ type: 'BACK' })}
      />
    );
  }

  if (status === 'MAX_DETAILS') {
    return (
      <MaxPlanDetails
        onBackClick={() => dispatch({ type: 'BACK' })}
        onSelectPlan={() => dispatch({ type: 'SELECT_PLAN', plan: 'max' })}
      />
    );
  }

  if (status === 'INFINITY_DETAILS') {
    return (
      <InfinityPlanDetails
        onBackClick={() => dispatch({ type: 'BACK' })}
        onSelectPlan={() => dispatch({ type: 'SELECT_PLAN', plan: 'infinity' })}
      />
    );
  }

  return null;
};

export default FccSetPlan;
